/**
 * Message Helper Utility
 * 
 * This utility provides functions to extract localized messages from API responses
 * based on the user's selected language (English or Arabic).
 * 
 * It supports graceful fallback to English when Arabic translations are missing
 * and provides development warnings for missing translations.
 */

/**
 * Extract localized message from API response based on current language
 * 
 * @param {Object} response - Axios response object
 * @param {Object} i18n - i18next instance
 * @returns {string} - Localized message
 * 
 * @example
 * const response = await axios.get('/api/endpoint');
 * const message = getLocalizedMessage(response, i18n);
 * toast.success(message);
 */
export const getLocalizedMessage = (response, i18n) => {
  // Check if response has data
  if (!response?.data) {
    return i18n.t('common.errorOccurred');
  }

  const { message, message_ar } = response.data;
  
  // If Arabic is selected and Arabic message exists, use it
  if (i18n.language === 'ar' && message_ar) {
    return message_ar;
  }
  
  // Fallback to English message
  return message || i18n.t('common.operationCompleted');
};

/**
 * Extract localized error message from error object
 * 
 * @param {Object} error - Axios error object
 * @param {Object} i18n - i18next instance
 * @returns {string} - Localized error message
 * 
 * @example
 * try {
 *   await axios.post('/api/endpoint', data);
 * } catch (error) {
 *   const message = getLocalizedErrorMessage(error, i18n);
 *   toast.error(message);
 * }
 */
export const getLocalizedErrorMessage = (error, i18n) => {
  // Check if error has response data
  if (!error?.response?.data) {
    return i18n.t('common.errorOccurred');
  }

  const { message, message_ar } = error.response.data;
  
  // If Arabic is selected and Arabic message exists, use it
  if (i18n.language === 'ar' && message_ar) {
    return message_ar;
  }
  
  // Fallback to English message
  return message || i18n.t('common.errorOccurred');
};

/**
 * Log warning if Arabic translation is missing (development only)
 * 
 * This function helps developers identify endpoints that are missing Arabic translations
 * during development. It does not log anything in production.
 * 
 * @param {Object} response - API response
 * @param {string} endpoint - API endpoint name for identification
 * 
 * @example
 * const response = await axios.get('/api/doctor/profile');
 * checkArabicTranslation(response, 'getDoctorProfile');
 */
export const checkArabicTranslation = (response, endpoint) => {
  if (process.env.NODE_ENV === 'development') {
    if (response?.data?.message && !response?.data?.message_ar) {
      console.warn(
        `[i18n] Missing Arabic translation for endpoint: ${endpoint}`,
        `English message: "${response.data.message}"`
      );
    }
  }
};
